import { useState } from 'react';
import { useLoaderData } from 'react-router';

import { Badge, Card } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { PageHeader } from '@/comuns/PageHeader';
import type { CargaDaPlataforma, EventoDeAuditoria, ImpersonacaoRegistrada } from '@/data/tipos';
import { DetalheDaAuditoriaModal } from './DetalheDaAuditoriaModal';

function quando(iso: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
}

export function Auditoria() {
    const carga = useLoaderData() as CargaDaPlataforma;
    const [busca, setBusca] = useState('');
    const [evento, setEvento] = useState<EventoDeAuditoria | null>(null);

    const lista = carga.auditoria.filter((e) => !busca
        || `${e.clinica} ${e.acao} ${e.tabela} ${e.autorNome}`.toLowerCase().includes(busca.toLowerCase()));

    const colunas: DataTableColumn<EventoDeAuditoria>[] = [
        { key: 'ocorridoEm', header: 'Quando', width: 160, render: (e) => quando(e.ocorridoEm) },
        { key: 'clinica', header: 'Clínica' },
        {
            key: 'acao', header: 'Ação',
            render: (e) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontWeight: 600 }}>{e.acao}</strong>
                    {e.acao.startsWith('plataforma.') && <Badge tone="info">plataforma</Badge>}
                </span>
            ),
        },
        { key: 'tabela', header: 'Tabela', width: 170 },
        { key: 'autorNome', header: 'Autor', width: 180 },
    ];

    const colunasDeImpersonacao: DataTableColumn<ImpersonacaoRegistrada>[] = [
        { key: 'iniciadaEm', header: 'Início', width: 160, render: (i) => quando(i.iniciadaEm) },
        { key: 'clinica', header: 'Clínica' },
        { key: 'operador', header: 'Operador', width: 200 },
        { key: 'motivo', header: 'Motivo' },
        {
            key: 'viva', header: 'Estado', width: 130,
            render: (i) => <Badge tone={i.viva ? 'warning' : 'neutral'} dot>{i.viva ? 'Aberta' : 'Encerrada'}</Badge>,
        },
    ];

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Log de auditoria"
                description="O que aconteceu na base, com quem, quando — e em qual clínica. Clique num evento para o detalhe."
            />

            {/* A ausência do diff é a decisão, não uma limitação: o que mudou pode
                ser dado de paciente. A exceção são as ações da própria plataforma,
                cujo detalhe a função do operador escreveu sem dado clínico. */}
            <Alert tone="info" dismissible={false} title="O log mostra que houve edição, não o que foi editado">
                A função que alimenta esta tela devolve ação, tabela, autor e horário — e deixa de fora as
                alterações, que podem conter dado clínico. Só as ações da própria plataforma (suspender, trocar
                plano, estornar…) mostram o detalhe, com motivo e de → para. Ver o conteúdo de uma clínica exige
                impersonação, que também fica registrada.
            </Alert>

            <FilterBar
                search={<SearchField value={busca} onChange={setBusca} placeholder="Buscar por ação, tabela, clínica ou autor" width={320} />}
                resultLabel={`${lista.length} ${lista.length === 1 ? 'evento' : 'eventos'}`}
            />

            <Card padding={0}>
                <DataTable
                    columns={colunas} rows={lista} rowKey="id"
                    onRowClick={setEvento}
                    /* A linha clicável ganha nome acessível: é a ação, e não o horário da primeira coluna. */
                    rowLabel={(e) => `${e.acao} · ${e.clinica} · ${quando(e.ocorridoEm)}`}
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum evento registrado."
                />
            </Card>

            <Card title="Impersonações" subtitle="Quem entrou em qual clínica, por quê, e se ainda está dentro" padding={0}>
                <DataTable
                    columns={colunasDeImpersonacao} rows={carga.impersonacoes} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma impersonação registrada — que é o estado saudável."
                />
            </Card>

            <DetalheDaAuditoriaModal evento={evento} onFechar={() => setEvento(null)} />
        </div>
    );
}
